import { type ReactNode, useEffect, useRef, useState } from 'react'
import { X } from 'lucide-react'
import { useAuthStore } from '@/shared/store/authStore'
import { RAIL_WIDTH_DEFAULT, useLayoutStore } from '@/shared/store/layoutStore'
import { hasHostShell } from '@/shared/bridge/env'
import { cn } from '@/shared/utils/cn'
import { Button, Logo, UiIcon } from '@/shared/ui'

const DRAWER_ANIMATION_MS = 180

/**
 * 햄버거 메뉴 공통 틀: 열고 닫는 애니메이션, 배경, 로고 헤더, 하단 사용자·로그아웃.
 * 메뉴 목록은 역할별 셸이 children 으로 넣는다. 열림 상태는 layoutStore.drawerOpen.
 */
export function DrawerFrame({
  roleLabel,
  onLogout,
  desktopHidden = true,
  children,
}: {
  roleLabel: string
  onLogout: () => void
  /** 데스크톱에 사이드바(Rail)가 따로 있으면 true */
  desktopHidden?: boolean
  children: ReactNode
}) {
  const open = useLayoutStore((state) => state.drawerOpen)
  const setOpen = useLayoutStore((state) => state.setDrawerOpen)
  const [mounted, setMounted] = useState(open)
  const [closing, setClosing] = useState(false)
  const closeTimerRef = useRef<number | null>(null)
  const user = useAuthStore((state) => state.user)

  useEffect(() => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }

    if (open) {
      setMounted(true)
      setClosing(false)
      return undefined
    }

    if (!mounted) return undefined
    setClosing(true)
    closeTimerRef.current = window.setTimeout(() => {
      setMounted(false)
      setClosing(false)
      closeTimerRef.current = null
    }, DRAWER_ANIMATION_MS)

    return () => {
      if (closeTimerRef.current !== null) {
        window.clearTimeout(closeTimerRef.current)
        closeTimerRef.current = null
      }
    }
  }, [mounted, open])

  useEffect(() => {
    if (!mounted) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.style.overflow = previous
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [mounted, setOpen])

  if (!mounted) return null

  return (
    <div className={cn('fixed inset-0 z-[9997]', desktopHidden && 'lg:hidden')}>
      <button
        type="button"
        className={cn(
          'absolute inset-0 bg-black/40',
          closing ? 'qsc-drawer-backdrop-out' : 'qsc-drawer-backdrop-in',
        )}
        onClick={() => setOpen(false)}
        aria-label="메뉴 닫기"
      />
      <aside
        className={cn(
          'relative flex h-full max-w-[86vw] flex-col bg-surface shadow-overlay',
          closing ? 'qsc-drawer-panel-out' : 'qsc-drawer-panel-in',
        )}
        // 너비는 SV 데스크톱 사이드바(Rail)와 같은 값을 쓴다.
        style={{ width: RAIL_WIDTH_DEFAULT }}
      >
        <div className="flex h-[calc(4.5rem+var(--safe-top))] items-end justify-between border-b border-border-subtle px-4 pb-4 pt-[var(--safe-top)]">
          <Logo />
          <Button variant="ghost" size="icon" onClick={() => setOpen(false)} aria-label="메뉴 닫기">
            <X aria-hidden className="size-5" />
          </Button>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-4">{children}</nav>
        <div className="border-t border-border-subtle px-3 pt-3 pb-[calc(var(--safe-bottom)+0.75rem)]">
          <div className="mb-2 flex items-center gap-3 rounded-md px-3 py-2">
            <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-brand-soft text-preset-label font-extrabold text-brand">
              {user?.userNm?.[0] ?? 'U'}
            </div>
            <div className="min-w-0">
              <p className="truncate text-preset-label text-text">{user?.userNm ?? '사용자'} 님</p>
              <p className="truncate text-preset-helper text-text-subtle">{roleLabel}</p>
            </div>
          </div>
          {!hasHostShell() && (
            <Button
              variant="ghost"
              className="w-full justify-start text-text-muted hover:text-danger"
              onClick={onLogout}
            >
              <UiIcon.LogOut aria-hidden className="size-4" />
              로그아웃
            </Button>
          )}
        </div>
      </aside>
    </div>
  )
}

// 크기·글씨는 SV 데스크톱 사이드바(Rail)와 같게 맞춘다. 점주는 데스크톱에서도 드로어를 쓴다.

/** 드로어 메뉴 그룹: 회색 소제목 + 메뉴 줄 목록 */
export function DrawerGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="mb-5 last:mb-0">
      <p className="mb-2 px-3 text-preset-helper font-bold text-text-subtle">{label}</p>
      <div className="flex flex-col gap-1">{children}</div>
    </div>
  )
}

/** 드로어 메뉴 한 줄의 공통 스타일 */
export const drawerItemClass = (selected: boolean) =>
  cn(
    'flex h-11 w-full items-center gap-3 rounded-md px-3 text-preset-label transition',
    selected ? 'bg-brand-soft text-brand' : 'text-text-muted hover:bg-gray-50 hover:text-text',
  )
